import { useCallback, useEffect, useRef, useState, useMemo, JSX } from 'react'

import { ChevronDown } from 'lucide-react'
import { FieldValues, Path, PathValue, useController } from 'react-hook-form'
import { toast } from 'sonner'

import { AddressSearchDropdown } from '~/components/component-library/AddressSearchInput/components/AddressSearchDropdown'
import { ValidAddressDisplay } from '~/components/component-library/AddressSearchInput/components/ValidAddressDisplay'
import {
  buildTabSections,
  cycleTab,
  getTabItems,
  resolveActiveTab,
} from '~/components/component-library/AddressSearchInput/tabs'
import {
  AddressSearchInputProps,
  AddressSearchTabId,
} from '~/components/component-library/AddressSearchInput/types'
import { useAddressSearchData } from '~/components/component-library/AddressSearchInput/useAddressSearchData'
import { Group } from '~/components/component-library/Group'
import { DebouncedInput } from '~/components/inputs/DebouncedInput'
import { ErrorFooter, visibleError } from '~/components/inputs/ErrorFooter'
import { AUTOCOMPLETE_SUPPRESS } from '~/components/inputs/Input'
import { Popover, PopoverAnchor } from '~/components/ui/popover'
import { useAuth } from '~/context/AuthProvider'
import { useKeydown } from '~/hooks/useKeyDown'
import { useListArrows } from '~/hooks/useListArrows'
import { IAddress, AddressSearchResult, VmType } from '~/types'
import { Address, AddressType } from '~/utils/address'
import { filledFieldFocusProps } from '~/utils/filledFieldFocus'
import { cn } from '~/utils/tailwind-utils'

export const AddressSearchInput = <FormType extends FieldValues>({
  control,
  inputName,
  filteredAddresses = [],
  setValue,
  filteredAddressErrorText,
  vmTypes,
  label,
  footer,
  placeholder,
  chainId,
  disabled,
  disabledReason,
  autoFocus,
  onSelect,
  onClear,
  size = 'base',
  optional = false,
  requiredErrorMessage = 'Required',
  includeBankAccounts = false,
}: AddressSearchInputProps<FormType>): JSX.Element => {
  const [query, setQuery] = useState<string>('')
  const [selectedAddress, setSelectedAddress] = useState<IAddress>()
  const [isFocused, setIsFocused] = useState<boolean>(false)
  // The tab the user explicitly navigated to; the effective tab is derived
  // from it so it can fall back when datasets or the query change.
  const [requestedTab, setRequestedTab] = useState<AddressSearchTabId>()
  const {
    addressSearchHistory: {
      searchHistory,
      searchPlaceholder,
      addToSearchHistory,
      removeFromSearchHistory,
    },
  } = useAuth()

  const definedVmTypes: VmType[] = useMemo(() => {
    if (vmTypes) {
      return vmTypes
    }

    return ['evm'] as const
  }, [vmTypes])

  // Bank liquidation addresses are EVM addresses, so banks can't be offered
  // in a search restricted to non-EVM types.
  if (includeBankAccounts && !definedVmTypes.includes('evm')) {
    throw new Error(
      'AddressSearchInput: includeBankAccounts requires vmTypes to include evm',
    )
  }

  const {
    addressResolution,
    ensResolution,
    filteredAddressesSet,
    tabData,
    isLoading,
  } = useAddressSearchData({
    query,
    chainId,
    filteredAddresses,
    searchHistory,
    searchPlaceholder,
    vmTypes: definedVmTypes,
    includeBankAccounts,
  })

  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const resultsRef = useRef<(HTMLButtonElement | null)[]>([])

  const hasQuery = !!query
  const tabItems = useMemo(
    () => getTabItems({ tabData, hasQuery }),
    [tabData, hasQuery],
  )
  const activeTab = resolveActiveTab({ requestedTab, tabItems })
  const sections = useMemo(
    () => buildTabSections({ tab: activeTab, tabData, hasQuery }),
    [activeTab, tabData, hasQuery],
  )

  const {
    field: { onBlur, value },
    fieldState,
    formState,
  } = useController({
    control,
    name: inputName,
    rules: {
      required: { value: !optional, message: requiredErrorMessage },
    },
  })

  const errorMessage = visibleError(fieldState.error, {
    isTouched: fieldState.isTouched,
    isEmpty: !value,
    isSubmitted: formState.isSubmitted,
  })

  const addressType: AddressType[] = useMemo(() => {
    return definedVmTypes.flatMap(Address.supportedTypes)
  }, [definedVmTypes])

  useEffect(() => {
    if (value && Address.validate(value, addressType)) {
      setQuery(value)
    }
    if (!value) {
      // Keep the visible query in sync when the form value is cleared
      // externally (e.g. reset()); otherwise the stale query re-resolves and
      // the field appears un-clearable.
      setSelectedAddress(undefined)
      setQuery('')
      setRequestedTab(undefined)
    }
  }, [value, addressType])

  const clearAddress = useCallback(() => {
    setSelectedAddress(undefined)
    setQuery('')
    setRequestedTab(undefined)
    const typedAddress = '' as PathValue<FormType, Path<FormType>>
    setValue(inputName, typedAddress)
    onSelect?.(typedAddress)
    onClear?.()
    // Focus the input after clearing
    setTimeout(() => inputRef.current?.focus(), 0)
  }, [inputName, onSelect, onClear, setValue])

  const selectResult = useCallback(
    (result: AddressSearchResult, keepFocus = false) => {
      if (filteredAddressesSet.has(result.address)) {
        toast.error(filteredAddressErrorText ?? 'Address is invalid')
        setQuery('')
        return
      }

      const typedAddress = result.address as PathValue<FormType, Path<FormType>>
      setValue(inputName, typedAddress, {
        shouldValidate: true,
      })
      onSelect?.(typedAddress)
      setSelectedAddress(result.address)
      setIsFocused(false)
      setRequestedTab(undefined)
      addToSearchHistory(result)
      // Keyboard-driven fills keep the field focused (the container is the
      // filled state's tab stop); mouse selection leaves focus where it was.
      if (keepFocus) {
        setTimeout(() => containerRef.current?.focus(), 0)
      }
    },
    [
      addToSearchHistory,
      filteredAddressesSet,
      inputName,
      onSelect,
      setValue,
      filteredAddressErrorText,
    ],
  )

  // Handle direct ens or address input
  useEffect(() => {
    if (selectedAddress) return // Ensure we don't infinitely loop
    if (Address.validate(query, addressType)) {
      // Typed/pasted directly — keep focus only if the user is in the input
      selectResult(
        { address: Address.format(query) },
        document.activeElement === inputRef.current,
      )
    }
  }, [
    addressResolution,
    ensResolution,
    query,
    addressType,
    selectResult,
    selectedAddress,
  ])

  // Keyboard focus moves through the active tab's rows in render order;
  // `removable` marks the rows Backspace can remove (recents).
  const focusable = useMemo(
    () =>
      sections.flatMap((section) =>
        section.rows.map((result) => ({
          result,
          removable: !!section.allowRemove,
        })),
      ),
    [sections],
  )

  const showResults =
    isFocused && !selectedAddress && (hasQuery || tabItems.length > 0)

  const { currentFocus, resetFocus, setFocus } = useListArrows(
    focusable.length,
    !showResults,
  )

  const handleTabChange = useCallback(
    (tab: AddressSearchTabId) => {
      setRequestedTab(tab)
      resetFocus()
    },
    [resetFocus],
  )

  useKeydown((e) => {
    if (e.key === 'Enter') {
      if (isFocused && !selectedAddress && currentFocus !== -1) {
        const focused = focusable[currentFocus]

        if (focused) {
          selectResult(focused.result, true)
        }
      }
    }
    if (e.key === 'Tab' && showResults && tabItems.length > 1) {
      // Mirror TokenSelector: while the dropdown is open, Tab cycles its tabs
      e.preventDefault()
      const next = cycleTab({ activeTab, tabItems, reverse: e.shiftKey })
      if (next) handleTabChange(next)
    }
    if (e.key === 'Backspace' && showResults && !inputRef.current?.value) {
      // With the input empty, Backspace removes the highlighted recent (the
      // keyboard counterpart of the row's X button). The live DOM value is
      // checked instead of the debounced query so a just-cleared character
      // can't race into a removal.
      const focused = focusable[currentFocus]
      if (focused?.removable) {
        e.preventDefault()
        removeFromSearchHistory(focused.result.address)
      }
    }
    if (e.key === 'Escape' && showResults) {
      // The popover's openness mirrors input focus, so blurring closes it.
      // Claim the keypress so a containing dialog doesn't also close.
      e.preventDefault()
      inputRef.current?.blur()
    }
  })

  useEffect(() => {
    if (isFocused && !selectedAddress && resultsRef.current[currentFocus]) {
      if (currentFocus === 0) {
        resultsRef.current[0]
          ?.closest('[data-scroll-container]')
          ?.scrollTo({ top: 0 })
      } else {
        resultsRef.current[currentFocus]?.scrollIntoView({
          block: 'nearest',
        })
      }
    }
  }, [currentFocus, isFocused, selectedAddress])

  return (
    <Group
      label={label}
      footer={errorMessage ? <ErrorFooter message={errorMessage} /> : footer}
    >
      <Popover open={showResults}>
        <PopoverAnchor asChild>
          <div
            ref={containerRef}
            {...filledFieldFocusProps({
              filled: !!selectedAddress,
              disabled,
              onClear: clearAddress,
            })}
            className={cn(
              'rounded-lg bg-surface-raised outline-none ring-1 ring-inset transition-shadow',
              errorMessage
                ? 'ring-error focus-within:ring-2 focus-within:ring-error'
                : 'ring-default focus-within:ring-2 focus-within:ring-focus',
              {
                'px-3': size === 'small',
                'px-3.5': size === 'base',
              },
            )}
          >
            {!selectedAddress ? (
              <div className="flex items-center">
                <DebouncedInput
                  ref={inputRef}
                  value={query}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => {
                    setIsFocused(false)
                    setRequestedTab(undefined)
                    onBlur()
                  }}
                  onChange={setQuery}
                  placeholder={placeholder ?? 'Search by name or address...'}
                  debounce={100}
                  autoFocus={autoFocus}
                  messagePositionClassName={'left-0'}
                  className="px-0"
                  rounded={false}
                  backgroundShadow={false}
                  focusRing={false}
                  disabled={disabled}
                  disabledReason={disabledReason}
                  autoComplete={AUTOCOMPLETE_SUPPRESS}
                  inputSize={size}
                />
                <ChevronDown
                  className={cn('flex-shrink-0 cursor-pointer', {
                    'size-4': size === 'small',
                    'size-5': size === 'base',
                    'text-error': errorMessage,
                    'text-secondary': query && !errorMessage,
                    'text-placeholder': !query && !errorMessage,
                    'pointer-events-none opacity-50': disabled,
                  })}
                  onMouseDown={(e) => {
                    e.preventDefault() // Prevent input blur
                    if (isFocused) {
                      inputRef.current?.blur()
                    } else {
                      inputRef.current?.focus()
                    }
                  }}
                />
              </div>
            ) : (
              <ValidAddressDisplay
                disabled={disabled}
                disabledReason={disabledReason}
                address={selectedAddress}
                onClearInput={clearAddress}
                chainId={chainId}
                size={size}
              />
            )}
          </div>
        </PopoverAnchor>
        <AddressSearchDropdown
          isLoading={isLoading}
          tabs={tabItems}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          sections={sections}
          highlightText={query}
          currentFocus={currentFocus}
          setFocus={setFocus}
          selectResult={selectResult}
          removeFromSearchHistory={removeFromSearchHistory}
          resultsRef={resultsRef}
        />
      </Popover>
    </Group>
  )
}

AddressSearchInput.displayName = 'AddressSearchInput'

export type { AddressSearchInputProps } from '~/components/component-library/AddressSearchInput/types'
